import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Typography,
  Autocomplete,
  Portal,
  Modal,
  Grid,
  InputAdornment,
  Switch,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'; 
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; 

import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Formik } from 'formik';
import { getSalesperson } from '../../service/User/getSalesperson';
import { getRegions } from '../../service/Address/getRegions';
import { qaddressSearch } from '../../service/Address/searchaddress';
import { getOfficeDetails } from '../../service/Office/getOfficeDetails';
import { getpropertySubType } from '../../service/Listings/getpropertyType';
import { getpropertyType } from '../../service/Listings/getpropertyType';
import { getContactTypes } from '../../service/Contacts/getContactTypes';
import { getContactsByCommunicationDetail } from '../../service/Contacts/getContacts';
import { getContactsByFirstLastName } from '../../service/Contacts/getContacts';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';
import { addContact } from '../../service/Contacts/addContact';
import { addListing } from '../../service/Listings/addListings';
import { getEnquirySource } from '../../service/Contacts/getenquirySource';
import { parseTimeStringToDate, roundToNearest5Minutes, formatTime } from '../../service/Util/dateTimeUtil';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';


const validationSchema = Yup.object().shape({
  // Address - required field
  Stateid: Yup.string().required('Address is required'),
  Suburbid: Yup.string().required('Address is required'),
  streetNumber: Yup.string().required('Street Number is required'),
  streetName: Yup.string().required('Address is required'),
  regionid: Yup.string().required('Region is required'),

  // Property Type - required field
  propertyType: Yup.string()
    .required('Property Type is required'),

  // Property Sub-Type - required field
  propertyTypeid: Yup.string()
    .required('Property Sub-Type is required'),

  propertySaleTypeid: Yup.string()
    .required('Sale Type is required'),
});


const SaleType = [
  { label: 'For Sale', value: '1' },
  { label: 'Auction', value: '2' },
  { label: 'Tender', value: '3' },
];

const RentalType = [
  { label: 'Lease', value: '4' },
];

const landSizeUnits = [
  { label: 'm²', value: 'squareMetre' },
  { label: 'ft²', value: 'squareFoot' },
  { label: 'Hectares', value: 'hectare' },
  { label: 'Acres', value: 'acre' }
];

const commercialRateType = [
  { label: 'Per Week', value: '300' },
  { label: 'Per Month', value: '301' },
  { label: 'Per Annum', value: '302' }
];

const leaseRateType = [
  { label: 'Per Week', value: '200' },
  { label: 'Per Month', value: '201' },
];

const holidayRateType = [
  { label: 'Per Week', value: '100' },
  { label: 'Per Month', value: '101' },
];

const ListingAdd = () => {
  const [Salesperson, setSalesperson] = useState([]);
  const [Regions, setRegions] = useState([]);
  const [OfficeDetails, setOfficeDetails] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [propertySubTypes, setPropertySubTypes] = useState([]);
  const [contactResultsEmail, setContactResultsEmail] = useState([]);
  const [contactResultsMobile, setContactResultsMobile] = useState([]);
  const [contactResultsFirstLastName, setcontactResultsFirstLastName] = useState([]);
  const [searchAddressResults, setSearchAddressResults] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [enquirySource, setEnquirySource] = useState([]);
  const [isSearchingMobile, setIsSearchingMobile] = useState(false);
  const [isSearchingEmail, setIsSearchingEmail] = useState(false);
  const [isSearchingName, setIsSearchingName] = useState(false);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [isSearchingMapbox, setIsSearchingMapbox] = useState(false);
  const [isSavingListing, setIsSavingListing] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedTime, setSelectedTime] = useState(dayjs());

  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [
        salespersonResponse,
        propertyTypeResponse,
        regionsResponse,
        officeDetailsResponse,
        contactTypesResponse,
        enquirySourceResponse
      ] = await Promise.all([
        getSalesperson("listing_view"),
        getpropertyType(),
        getRegions(),
        getOfficeDetails(),
        getContactTypes(),
        getEnquirySource()
      ]);

      setSalesperson(salespersonResponse.data);
      setPropertyTypes(propertyTypeResponse.data);
      setRegions(regionsResponse.data);
      setOfficeDetails(officeDetailsResponse.data);
      setContactTypes(contactTypesResponse.data);
      setEnquirySource(enquirySourceResponse.data);

    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchContacts = useDebouncedCallback(async (mobile, email, source) => {
    if (source === 'mobile' && mobile) {
      setIsSearchingMobile(true);
    } else if (source === 'email' && email) {
      setIsSearchingEmail(true);
    }
    try {
      const contacts = await getContactsByCommunicationDetail(mobile, email);

      if (contacts.data && contacts.data.length > 0) {
        if (source === 'email') {
          setContactResultsEmail(contacts.data);
        } else {
          setContactResultsMobile(contacts.data);
        }
      } else {
        setContactResultsMobile([]);
        setContactResultsEmail([]);
      }
    } catch (error) {
      setContactResultsMobile([]);
      setContactResultsEmail([]);
    }

    if (source === 'mobile' && mobile) {
      setIsSearchingMobile(false);
    } else if (source === 'email' && email) {
      setIsSearchingEmail(false);
    }
  }, 500);

  const searchContactsFirstLastName = useDebouncedCallback(async (firstname, lastname) => {
    setIsSearchingName(true);
    try {
      const contactsFNResults = await getContactsByFirstLastName(firstname, lastname);

      if (contactsFNResults.data && contactsFNResults.data.length > 0) {
        setcontactResultsFirstLastName(contactsFNResults.data);
      } else {
        setcontactResultsFirstLastName([]);
      }
    } catch (error) {
      setcontactResultsFirstLastName([]);
    }
    setIsSearchingName(false);
  }, 500);


  const fetchPropertySubTypes = useCallback(async (type) => {
    try {
      const response = await getpropertySubType(type);
      setPropertySubTypes(response.data);
      return response.data;
    } catch (error) {
      return [];
    }
  }, []);

  const searchAddress = useDebouncedCallback(async (searchAddressText) => {
    setIsSearchingMapbox(true);
    if ((searchAddressText && searchAddressText.length > 3)) {
      let queryString = `q=${searchAddressText}`;
      let countryCode = OfficeDetails.countryId === 152 ? 'NZ' : 'AU';

      if (OfficeDetails.latitude && OfficeDetails.longitude) {
        queryString = `${queryString}&proximity=${OfficeDetails.longitude},${OfficeDetails.latitude}&country=${countryCode}&limit=5`;
      } else {
        queryString = `${queryString}&country=${countryCode}&limit=5`;
      }

      const addressResults = await qaddressSearch(queryString);
      if (addressResults.data && addressResults.data.length > 0) {
        setSearchAddressResults(addressResults.data);
      } else {
        setSearchAddressResults([]);
      }
    }
    setIsSearchingMapbox(false);
  }, 500);

  const handleSubmit = async (values) => {
    setIsSavingListing(true);
    try {
      let sellerContactId = '';
      let auctionDateTime = '';
      if (values.isSearchContact === 1) {
        sellerContactId = values.listingcontactId;
      } else {
        const resultsContact = await addContact(values);
        sellerContactId = resultsContact.data?.contactID;
      }

      if (!sellerContactId) {
        setIsSavingListing(false);
        return;
      }

      if (values.auctionDate && values.auctionTime) {
        const formattedAuctionDate = values.auctionDate ? dayjs(values.auctionDate).format('YYYY-MM-DD') : '';
        auctionDateTime = `${formattedAuctionDate} ${values.auctionTime}`;
      }

      const listingJSON = {
        ...values,
        sellerContactId,
        auctionDateTime
      };
      const resultsAddListing = await addListing(listingJSON);

      if (resultsAddListing.status === 200 || resultsAddListing.status === 201) {
        navigate(`/listing/edit/${resultsAddListing.data?.propertyID}`);
      }
      setIsSavingListing(false);
    } catch (error) {
      setIsSavingListing(false);
    }
  };

  const getRateTypeOptions = (propertyType) => {
    switch (propertyType) {
      case 'CL':
        return commercialRateType;
      case 'H':
        return holidayRateType;
      case 'L':
        return leaseRateType;
      default:
        return [];
    }
  };

  return (
    // Wrap the entire component with LocalizationProvider
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
      <Formik
        initialValues={{
          searchFirstName: '',
          searchLastName: '',
          searchMobile: '',
          searchEmail: '',
          firstNameContact: '',
          lastNameContact: '',
          contactTypeID: '',
          salespersonid: '',
          contactEmail: '',
          contactNumber: '',
          enableSMSBroadcast: 0,
          enabledForBroadCast: 0,
          isAddContact: 0,
          isSearchContact: 0,
          listingcontactId: '',
          fullAddress: '',
          userid: '',
          userIdSp2: '',
          streetNumber: '',
          streetName: '',
          regionid: '',
          Suburbid: '',
          Stateid: '',
          postcode: '',
          priceFrom: '',
          priceTo: '',
          propertySaleTypeid: '',
          rentalPrice: '',
          propertyType: '',
          propertyTypeid: '',
          contactSourceId: '',
          auctionDate: null,
          auctionTime: '',
          auctionDetails: '',
          listingDate: dayjs(),
          expiryDate: null,
          agreementDate: null,
          /*Res */
          bedrooms: '',
          bathrooms: '',
          carspaces: '',
          garages: '',
          carports: '',
          landsizeunit: '',
          landsize: '',
          buildingArea: '',
          airConditioning: '',
          study: '',
          pool: '',
          councilRates: '',
          zoneCode: '',
          landDepth: '',
          landWidth: '',
          waterRates: '',
          strataLevy: '',
          energyRating: '',
          newBuild: '',
          /*Com */
          trussHeight: '',
          hardStand: '',
          insurance: '',
          landTax: '',
          /*Rental */
          maxsleep: '',
          furnished: '',
          pets: '',
          availableDate: null,
          availableDateEnd: null,
          bond: '',
          rateTypeId: '',
          leaseStartDate: null,
          leaseEndDate: null,
          leasePrice: '',
          /*Buiness*/
          internalOfficeCode: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleBlur, handleSubmit, values, errors, touched, setFieldValue }) => (
          <div className="formContainer">
            {/* Address Section */}
            <div className="formSection">
              <div>
                <Grid container spacing={2}>
                  {/* Street Number */}
                  <Grid >
                    {touched.streetNumber && errors.streetNumber && (
                      <Typography sx={{ color: 'red', fontSize: 12, marginTop: 1 }}>
                        {errors.streetNumber}
                      </Typography>
                    )}
                    <TextField
                      label="Street No"
                      value={values.streetNumber}
                      onChange={handleChange('streetNumber')}
                      onBlur={handleBlur('streetNumber')}
                      error={touched.streetNumber && !!errors.streetNumber}
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  {/* Address Input */}
                  <Grid item xs={12}>
                    <Typography className="addressLabel">Address</Typography>
                    {!isAddressSelected ? (
                      <>
                        {((touched.Stateid && errors.Stateid) ||
                          (touched.Suburbid && errors.Suburbid) ||
                          (touched.streetName && errors.streetName)) && (
                            <Typography sx={{ color: 'red', fontSize: 12, marginTop: 1 }}>
                              Address is required
                            </Typography>
                          )}
                        <TextField
                          label="Street Name, Suburb, State, Postcode"
                          onChange={(e) => {
                            setFieldValue('fullAddress', e.target.value);
                            searchAddress(e.target.value);
                          }}
                          value={values.fullAddress}
                          error={
                            (touched.Stateid && errors.Stateid) ||
                            (touched.Suburbid && errors.Suburbid) ||
                            (touched.streetName && errors.streetName)
                          }
                          fullWidth
                        />

                        {isSearchingMapbox && (
                          <Grid container alignItems="center" sx={{ padding: 2 }}>
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                            <Typography sx={{ color: '#555' }}>Searching addresses…</Typography>
                          </Grid>
                        )}

                        {/* Address Search Results */}
                        {searchAddressResults.length > 0 && !isSearchingMapbox && (
                          <Card sx={{ backgroundColor: '#f5f5f5', mt: 1 }}>
                            <CardContent>
                              {searchAddressResults.map((item, index) => (
                                <Button
                                  key={index}
                                  startIcon={<LocationOnIcon />}
                                  onClick={() => {
                                    setFieldValue('Stateid', item.stateId);
                                    setFieldValue('Suburbid', item.suburbId);
                                    setFieldValue('streetName', item.streetName);
                                    setFieldValue('fullAddress', item.fullAddress);
                                    setFieldValue('postcode', item.postCode);
                                    setIsAddressSelected(true);
                                    setSearchAddressResults([]);
                                  }}
                                  sx={{ mb: 1, textTransform: 'none' }}
                                  fullWidth
                                  variant="outlined"
                                >
                                  {item.fullAddress}
                                </Button>
                              ))}
                            </CardContent>
                          </Card>
                        )}
                      </>
                    ) : (
                      <>
                        <Card sx={{ borderRadius: 1, mt: 2, backgroundColor: '#f5f5f5' }}>
                        <CardHeader
                            avatar={<LocationOnIcon />}
                            title={<Typography variant="body1" sx={{ fontSize: 16 }}>Selected Address</Typography>}
                          />
                          <CardContent>
                            <Typography sx={{ fontSize: 16, mb: 1 }}>
                              {values.fullAddress}
                            </Typography>
                            <Button
                              onClick={() => {
                                setIsAddressSelected(false);
                                setFieldValue('fullAddress', '');
                                setFieldValue('Stateid', '');
                                setFieldValue('Suburbid', '');
                                setFieldValue('streetName', '');
                                setFieldValue('postcode', '');
                              }}
                            >
                              Change Address
                            </Button>
                          </CardContent>
                        </Card>
                      </>
                    )}
                  </Grid>
                </Grid>
              </div>

              <div>
                <Typography className="addressLabel">Regions</Typography>
                {touched.regionid && errors.regionid && (
                  <Typography sx={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                    {errors.regionid}
                  </Typography>
                )}

                <Autocomplete
                  options={Regions}
                  getOptionLabel={(option) => option.regionName}
                  value={Regions.find((r) => r.regionid === values.regionid) || null}
                  onChange={(event, newValue) => {
                    setFieldValue('regionid', newValue ? newValue.regionid : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Region"
                      error={touched.regionid && Boolean(errors.regionid)}
                      helperText={touched.regionid && errors.regionid}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.regionid === value.regionid}
                />
              </div>

              <div className="addressRow">
                <div className="addressField">
                  <Typography className="addressLabel">Property Type</Typography>
                  {touched.propertyType && errors.propertyType && (
                    <Typography sx={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                      {errors.propertyType}
                    </Typography>
                  )}
                  <Autocomplete
                    options={propertyTypes}
                    getOptionLabel={(option) => option.title}
                    value={
                      propertyTypes.find((pt) => pt.type === values.propertyType) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue('propertyType', newValue ? newValue.type : '');
                      if (newValue) {
                        fetchPropertySubTypes(newValue.type);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Property Type"
                        error={touched.propertyType && Boolean(errors.propertyType)}
                        helperText={touched.propertyType && errors.propertyType}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.type === value.type}
                    sx={{
                      mb: 2,
                      '& .MuiAutocomplete-inputRoot': {
                        backgroundColor: '#f9f9f9',
                        borderRadius: '10px',
                      },
                    }}
                  />
                </div>

                <div className="addressField">
                  <Typography className="addressLabel">Property Sub-Type</Typography>
                  {touched.propertyTypeid && errors.propertyTypeid && (
                    <Typography sx={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                      {errors.propertyTypeid}
                    </Typography>
                  )}
                  <Autocomplete
                    options={propertySubTypes}
                    getOptionLabel={(option) => option.name}
                    value={
                      propertySubTypes.find((subtype) => subtype.PropertyTypeID === values.propertyTypeid) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue('propertyTypeid', newValue ? newValue.PropertyTypeID : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Property Sub Type"
                        placeholder="Search..."
                        error={touched.propertyTypeid && Boolean(errors.propertyTypeid)}
                        helperText={touched.propertyTypeid && errors.propertyTypeid}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.PropertyTypeID === value.PropertyTypeID}
                    sx={{
                      mb: 2,
                      '& .MuiAutocomplete-inputRoot': {
                        backgroundColor: '#f9f9f9',
                        borderRadius: '10px',
                      },
                    }}
                  />
                </div>
              </div>

              <Typography className="sectionTitleDropDown">{((values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L') || values.propertySaleTypeid === '4') ? "" : "Sale Type"}</Typography>
              {touched.propertySaleTypeid && errors.propertySaleTypeid && (
                <Typography sx={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                  {errors.propertySaleTypeid}
                </Typography>
              )}
              <Autocomplete
                options={
                  (values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L' || values.propertySaleTypeid === '4')
                    ? RentalType
                    : SaleType
                }
                getOptionLabel={(option) => option.label}
                value={
                  ((values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L' || values.propertySaleTypeid === '4')
                    ? RentalType
                    : SaleType
                  ).find((type) => type.value === values.propertySaleTypeid) || null
                }
                onChange={(event, newValue) => {
                  const newValueId = newValue ? newValue.value : '';
                  setFieldValue('propertySaleTypeid', newValueId);

                  if (newValueId !== '2' && newValueId !== '3') {
                    setFieldValue('auctionDate', null);
                    setFieldValue('auctionTime', '');
                    setFieldValue('auctionDetails', '');
                  }
                  if (newValueId !== '4') {
                    setFieldValue('rateTypeId', '');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Sale Type"
                    error={touched.propertySaleTypeid && Boolean(errors.propertySaleTypeid)}
                    helperText={touched.propertySaleTypeid && errors.propertySaleTypeid}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                sx={{
                  mb: 2,
                  '& .MuiAutocomplete-inputRoot': {
                    backgroundColor: '#f9f9f9',
                    borderRadius: '10px',
                  },
                }}
              />

              <Typography className="sectionTitleDropDown">{values.propertySaleTypeid === '4' ? 'Property Manager' : 'Lister'}</Typography>
              <Autocomplete
                options={Salesperson}
                getOptionLabel={(option) => option.salespersonName}
                value={Salesperson.find((sp) => sp.userid === values.userid) || null}
                onChange={(event, newValue) => {
                  setFieldValue('userid', newValue ? newValue.userid : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Salesperson"
                    placeholder="Search..."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.userid === value.userid}
                sx={{
                  mb: 2,
                  '& .MuiAutocomplete-inputRoot': {
                    backgroundColor: '#f9f9f9',
                    borderRadius: '10px',
                  },
                }}
              />

              <Typography className="sectionTitleDropDown">{values.propertySaleTypeid === '4' ? 'Secondary Property Manager' : 'Secondary Lister'}</Typography>
              <Autocomplete
                options={Salesperson}
                getOptionLabel={(option) => option.salespersonName}
                value={Salesperson.find((sp) => sp.userid === values.userIdSp2) || null}
                onChange={(event, newValue) => {
                  setFieldValue('userIdSp2', newValue ? newValue.userid : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Salesperson"
                    placeholder="Search..."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.userid === value.userid}
                sx={{
                  mb: 2,
                  '& .MuiAutocomplete-inputRoot': {
                    backgroundColor: '#f9f9f9',
                    borderRadius: '10px',
                  },
                }}
              />
            </div>

            <Typography className="sectionTitleDropDown">Dates</Typography>

            <div className="row">
              <div className="halfWidth">
                <DatePicker
                    label="Listing Date"
                    value={values.listingDate}
                    onChange={(newValue) => setFieldValue('listingDate', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="streetNameInput"
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {params.InputProps.endAdornment}
                                <IconButton>
                                  <CalendarTodayIcon />
                                </IconButton>
                              </>
                            ),
                          }
                        }}
                      />
                    )}
                  />

                {/* Same for Expiry Date */}
                <DatePicker
                  label="Expiry Date"
                  value={values.expiryDate}
                  onChange={(newValue) => setFieldValue('expiryDate', newValue)}
                  inputFormat="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth 
                      className="streetNameInput"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <IconButton>
                                <CalendarTodayIcon />
                              </IconButton>
                            </>
                          ),
                        }
                      }}
                    />
                  )}
                />

              {/* Office Agreement Start Date */}
                <DatePicker
                  label="Office Agreement Start Date"
                  value={values.agreementDate}
                  onChange={(newValue) => setFieldValue('agreementDate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      className="streetNameInput"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <IconButton >
                                <CalendarTodayIcon />
                              </IconButton>
                            </>
                          ),
                        }
                      }}
                    />
                  )}
                />

                {(values.propertySaleTypeid === '2' || values.propertySaleTypeid === '3') ? (
                  <>
                    {/* Date field — Auction or Tender Date */}
                    <DatePicker
                      label={values.propertySaleTypeid === '2' ? 'Auction Date' : 'Tender Date'}
                      value={values.auctionDate}
                      onChange={(newValue) => setFieldValue('auctionDate', newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          className="streetNameInput"
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <IconButton>
                                    <CalendarTodayIcon />
                                  </IconButton>
                                </>
                              ),
                            }
                          }}
                        />
                      )}
                    />

                    {/* Time field — Auction or Tender Time */}
                   <TimePicker
                        label={values.propertySaleTypeid === '2' ? 'Auction Time' : 'Tender Time'}
                        value={selectedTime}
                        onChange={(newValue) => {
                          const roundedTime = roundToNearest5Minutes(newValue);
                          setSelectedTime(roundedTime);
                          const formattedTime = formatTime(roundedTime);
                          setFieldValue('auctionTime', formattedTime);
                        }}
                        minutesStep={5}
                        ampm={false}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            value={values.auctionTime}
                            onClick={() => {
                              const date = parseTimeStringToDate(values.auctionTime);
                              setSelectedTime(dayjs(date));
                            }}
                            fullWidth
                            className="streetNameInput"
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {params.InputProps.endAdornment}
                                    <IconButton>
                                      <AccessTimeIcon />
                                    </IconButton>
                                  </>
                                ),
                                readOnly: true,
                              }
                            }}
                            sx={{ mt: 2 }}
                          />
                        )}
                      />

                    {/* Auction/Tender Details text input */}
                    <TextField
                      label={values.propertySaleTypeid === '2' ? 'Auction Details' : 'Tender Details'}
                      value={values.auctionDetails}
                      onChange={handleChange('auctionDetails')}
                      onBlur={handleBlur('auctionDetails')}
                      fullWidth
                      className="streetNameInput"
                      multiline
                      rows={3}
                      sx={{ mt: 2 }}
                    />

                  </>
                ) : null}

              </div>
            </div>

            <div className="formSection">
              <Typography className="sectionTitle">Extra Details</Typography>
              <Grid container spacing={2}>
                {/* Price From */}
                <Grid >
                  <TextField
                    label="Price From"
                    value={values.priceFrom}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue('priceFrom', value);
                      if (values.propertySaleTypeid === '4') {
                        setFieldValue('rentalPrice', value);
                      }
                    }}
                    onBlur={handleBlur('priceFrom')}
                    type="number"
                    variant="outlined"
                    slotProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    fullWidth
                  />
                </Grid>

                {/* Price To */}
                <Grid >
                  <TextField
                    label="Price To"
                    value={values.priceTo}
                    onChange={handleChange('priceTo')}
                    onBlur={handleBlur('priceTo')}
                    type="number"
                    variant="outlined"
                    slotProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              {(values.propertySaleTypeid === '4' && (values.propertyType === 'CL' || values.propertyType === 'H' || values.propertyType === 'L')) ? (
                <>
                  <Autocomplete
                    options={getRateTypeOptions(values.propertyType)}
                    getOptionLabel={(option) => option.label}
                    value={getRateTypeOptions(values.propertyType).find((option) => option.value === values.rateTypeId) || null}
                    onChange={(event, newValue) => {
                      setFieldValue('rateTypeId', newValue ? newValue.value : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Rate Type"
                        placeholder="Select Rate Type"
                        sx={{ mt: 1 }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </>
              ) : null}

              {/*Residential*/}

              {values.propertyType === 'R' ? (
                <>
                  <Grid container spacing={2}>
                    {/* Bedrooms */}
                    <Grid >
                      <TextField
                        label="Bedrooms"
                        value={values.bedrooms}
                        onChange={(e) => setFieldValue('bedrooms', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Bathrooms */}
                    <Grid >
                      <TextField
                        label="Bathrooms"
                        value={values.bathrooms}
                        onChange={(e) => setFieldValue('bathrooms', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Car Spaces */}
                    <Grid >
                      <TextField
                        label="Car Spaces"
                        value={values.carspaces}
                        onChange={(e) => setFieldValue('carspaces', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Carports */}
                    <Grid >
                      <TextField
                        label="Carports"
                        value={values.carports}
                        onChange={(e) => setFieldValue('carports', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Garages */}
                    <Grid >
                      <TextField
                        label="Garages"
                        value={values.garages}
                        onChange={(e) => setFieldValue('garages', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Swimming Pools */}
                    <Grid >
                      <TextField
                        label="Swimming Pools"
                        value={values.pool}
                        onChange={(e) => setFieldValue('pool', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Study */}
                    <Grid >
                      <TextField
                        label="Study"
                        value={values.study}
                        onChange={(e) => setFieldValue('study', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Floor Area */}
                    <Grid >
                      <TextField
                        label="Floor Area (m²)"
                        value={values.buildingArea}
                        onChange={(e) => setFieldValue('buildingArea', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Land Size */}
                    <Grid >
                      <TextField
                        label="Land Size"
                        value={values.landsize}
                        onChange={(e) => setFieldValue('landsize', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Land Size Unit */}
                    <Grid >
                      <Autocomplete
                        options={landSizeUnits}
                        getOptionLabel={(option) => option.label}
                        value={landSizeUnits.find((unit) => unit.value === values.landsizeunit) || null}
                        onChange={(event, newValue) => {
                          setFieldValue('landsizeunit', newValue ? newValue.value : '');
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Unit" placeholder="Unit" variant="outlined" />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Grid>

                    {/* Land Width */}
                    <Grid >
                      <TextField
                        label="Land Width (m)"
                        value={values.landWidth}
                        onChange={(e) => setFieldValue('landWidth', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Land Depth */}
                    <Grid >
                      <TextField
                        label="Land Depth (m)"
                        value={values.landDepth}
                        onChange={(e) => setFieldValue('landDepth', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Zoning */}
                    <Grid >
                      <TextField
                        label="Zoning"
                        value={values.zoneCode}
                        onChange={(e) => setFieldValue('zoneCode', e.target.value)}
                        slotProps={{ maxLength: 50 }}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Council Rates */}
                    <Grid >
                      <TextField
                        label="Council Rates"
                        value={values.councilRates}
                        onChange={(e) => setFieldValue('councilRates', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Water Rates */}
                    <Grid >
                      <TextField
                        label="Water Rates"
                        value={values.waterRates}
                        onChange={(e) => setFieldValue('waterRates', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Strata Levies */}
                    <Grid >
                      <TextField
                        label="Strata Levies"
                        value={values.strataLevy}
                        onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Energy Efficiency Rating */}
                    <Grid >
                      <TextField
                        label="Energy Efficiency Rating"
                        value={values.energyRating}
                        onChange={(e) => setFieldValue('energyRating', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* New Build */}
                    <Grid >
                      <Typography variant="subtitle1" gutterBottom>
                        New Build/Home
                      </Typography>
                      <Autocomplete
                        options={[
                          { label: 'N/A', value: '' },
                          { label: 'Yes', value: '1' },
                          { label: 'No', value: '0' },
                        ]}
                        getOptionLabel={(option) => option.label}
                        value={
                          [
                            { label: 'N/A', value: '' },
                            { label: 'Yes', value: '1' },
                            { label: 'No', value: '0' },
                          ].find((option) => option.value === values.newBuild) || null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue('newBuild', newValue ? newValue.value : '');
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select..." variant="outlined" />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Grid>

                    {/* Air Conditioning */}
                    <Grid >
                      <Typography variant="subtitle1" gutterBottom>
                        Air Conditioning
                      </Typography>
                      <Autocomplete
                        options={[
                          { label: 'Yes', value: '1' },
                          { label: 'No', value: '0' },
                        ]}
                        getOptionLabel={(option) => option.label}
                        value={
                          [
                            { label: 'Yes', value: '1' },
                            { label: 'No', value: '0' },
                          ].find((option) => option.value === values.airConditioning) || null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue('airConditioning', newValue ? newValue.value : '');
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select..." variant="outlined" />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : null}

              {/*Commercial For Sale and Commerical For Lease*/}
              {(values.propertyType === 'C' || values.propertyType === 'CL') ? (
                <>
                  <Grid container spacing={2}>
                    {/* Land Size */}
                    <Grid >
                      <TextField
                        label="Land Size (m²)"
                        value={values.landSize}
                        onChange={(e) => setFieldValue('landSize', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Building Area */}
                    <Grid >
                      <TextField
                        label="Building Area (m²)"
                        value={values.buildingArea}
                        onChange={(e) => setFieldValue('buildingArea', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Land Tax */}
                    <Grid >
                      <TextField
                        label="Land Tax"
                        value={values.landTax}
                        onChange={(e) => setFieldValue('landTax', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Insurance */}
                    <Grid >
                      <TextField
                        label="Insurance"
                        value={values.insurance}
                        onChange={(e) => setFieldValue('insurance', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Zoning */}
                    <Grid >
                      <TextField
                        label="Zoning"
                        value={values.zoneCode}
                        onChange={(e) => setFieldValue('zoneCode', e.target.value)}
                        variant="outlined"
                        slotProps={{ maxLength: 50 }}
                        fullWidth
                      />
                    </Grid>

                    {/* Council Rates */}
                    <Grid >
                      <TextField
                        label="Council Rates"
                        value={values.councilRates}
                        onChange={(e) => setFieldValue('councilRates', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Water Rates */}
                    <Grid >
                      <TextField
                        label="Water Rates"
                        value={values.waterRates}
                        onChange={(e) => setFieldValue('waterRates', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Strata Levies */}
                    <Grid >
                      <TextField
                        label="Strata Levies"
                        value={values.strataLevy}
                        onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Hardstand Area */}
                    <Grid >
                      <TextField
                        label="Hardstand Area (m²)"
                        value={values.hardStand}
                        onChange={(e) => setFieldValue('hardStand', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Truss Height */}
                    <Grid >
                      <TextField
                        label="Truss Height (m)"
                        value={values.trussHeight}
                        onChange={(e) => setFieldValue('trussHeight', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Energy Efficiency Rating */}
                    <Grid >
                      <TextField
                        label="Energy Efficiency Rating"
                        value={values.energyRating}
                        onChange={(e) => setFieldValue('energyRating', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </>
              ) : null}
              {/*Rental Holiday Homes and Res*/}
              {(values.propertyType === 'H' || values.propertyType === 'L') && (
                <>
                  {/* Row 1 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <TextField
                        label="Bedrooms"
                        type="number"
                        value={values.bedrooms}
                        onChange={(e) => setFieldValue('bedrooms', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {values.propertyType === 'H' && (
                      <Grid >
                        <TextField
                          label="Max Sleep"
                          type="number"
                          value={values.maxsleep}
                          onChange={(e) => setFieldValue('maxsleep', e.target.value)}
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                    )}
                  </Grid>

                  {/* Row 2 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <TextField
                        label="Bathrooms"
                        type="number"
                        value={values.bathrooms}
                        onChange={(e) => setFieldValue('bathrooms', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    <Grid >
                      <TextField
                        label="Car Spaces"
                        type="number"
                        value={values.carspaces}
                        onChange={(e) => setFieldValue('carspaces', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  {/* Row 3 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <TextField
                        label="Carports"
                        type="number"
                        value={values.carports}
                        onChange={(e) => setFieldValue('carports', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    <Grid >
                      <TextField
                        label="Garages"
                        type="number"
                        value={values.garages}
                        onChange={(e) => setFieldValue('garages', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  {/* Row 4 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <TextField
                        label="Swimming Pools"
                        type="number"
                        value={values.pool}
                        onChange={(e) => setFieldValue('pool', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    <Grid >
                      <TextField
                        label="Study"
                        type="number"
                        value={values.study}
                        onChange={(e) => setFieldValue('study', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  {/* Row 5 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <DatePicker
                        label="Date Available"
                        value={values.availableDate}
                        onChange={(newValue) => setFieldValue('availableDate', newValue)}
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" fullWidth />
                        )}
                      />
                    </Grid>

                    {values.propertyType === 'H' && (
                      <Grid >
                        <DatePicker
                          label="Available Date End"
                          value={values.availableDateEnd}
                          onChange={(newValue) => setFieldValue('availableDateEnd', newValue)}
                          renderInput={(params) => (
                            <TextField {...params} variant="outlined" fullWidth />
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {/* Row 6 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <TextField
                        label="Bond"
                        type="number"
                        value={values.bond}
                        onChange={(e) => setFieldValue('bond', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  {/* Row 7 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <Typography variant="subtitle1" gutterBottom>
                        Air Conditioning
                      </Typography>
                      <Autocomplete
                        options={[
                          { label: 'Yes', value: '1' },
                          { label: 'No', value: '0' }
                        ]}
                        getOptionLabel={(option) => option.label}
                        value={
                          [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
                            .find((option) => option.value === values.airConditioning) || null
                        }
                        onChange={(event, newValue) =>
                          setFieldValue('airConditioning', newValue ? newValue.value : '')
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select..." variant="outlined" />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                      />
                    </Grid>

                    <Grid >
                      <Typography variant="subtitle1" gutterBottom>
                        Pets
                      </Typography>
                      <Autocomplete
                        options={[
                          { label: 'Yes', value: '1' },
                          { label: 'No', value: '0' }
                        ]}
                        getOptionLabel={(option) => option.label}
                        value={
                          [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
                            .find((option) => option.value === values.pets) || null
                        }
                        onChange={(event, newValue) =>
                          setFieldValue('pets', newValue ? newValue.value : '')
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select..." variant="outlined" />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* Row 8 */}
                  <Grid container spacing={2}>
                    <Grid >
                      <Typography variant="subtitle1" gutterBottom>
                        Furnished
                      </Typography>
                      <Autocomplete
                        options={[
                          { label: 'Yes', value: '1' },
                          { label: 'No', value: '0' }
                        ]}
                        getOptionLabel={(option) => option.label}
                        value={
                          [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
                            .find((option) => option.value === values.furnished) || null
                        }
                        onChange={(event, newValue) =>
                          setFieldValue('furnished', newValue ? newValue.value : '')
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select..." variant="outlined" />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              {/* Rural */}
              {values.propertyType === 'RU' ? (
                <>
                  <Grid container spacing={2}>
                    {/* Bedrooms */}
                    <Grid >
                      <TextField
                        label="Bedrooms"
                        value={values.bedrooms}
                        onChange={(e) => setFieldValue('bedrooms', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Bathrooms */}
                    <Grid >
                      <TextField
                        label="Bathrooms"
                        value={values.bathrooms}
                        onChange={(e) => setFieldValue('bathrooms', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Car Spaces */}
                    <Grid >
                      <TextField
                        label="Car Spaces"
                        value={values.carspaces}
                        onChange={(e) => setFieldValue('carspaces', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Carports */}
                    <Grid >
                      <TextField
                        label="Carports"
                        value={values.carports}
                        onChange={(e) => setFieldValue('carports', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Garages */}
                    <Grid >
                      <TextField
                        label="Garages"
                        value={values.garages}
                        onChange={(e) => setFieldValue('garages', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Swimming Pools */}
                    <Grid >
                      <TextField
                        label="Swimming Pools"
                        value={values.pool}
                        onChange={(e) => setFieldValue('pool', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Study */}
                    <Grid >
                      <TextField
                        label="Study"
                        value={values.study}
                        onChange={(e) => setFieldValue('study', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{ maxLength: 2 }}
                        fullWidth
                      />
                    </Grid>

                    {/* Energy Efficiency Rating */}
                    <Grid >
                      <TextField
                        label="Energy Efficiency Rating"
                        value={values.energyRating}
                        onChange={(e) => setFieldValue('energyRating', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    {/* Zoning */}
                    <Grid >
                      <TextField
                        label="Zoning"
                        value={values.zoneCode}
                        onChange={(e) => setFieldValue('zoneCode', e.target.value)}
                        variant="outlined"
                        slotProps={{ maxLength: 50 }}
                        fullWidth
                      />
                    </Grid>

                    {/* Council Rates */}
                    <Grid >
                      <TextField
                        label="Council Rates"
                        value={values.councilRates}
                        onChange={(e) => setFieldValue('councilRates', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Water Rates */}
                    <Grid >
                      <TextField
                        label="Water Rates"
                        value={values.waterRates}
                        onChange={(e) => setFieldValue('waterRates', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Strata Levies */}
                    <Grid >
                      <TextField
                        label="Strata Levies"
                        value={values.strataLevy}
                        onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    {/* Land Size */}
                    <Grid >
                      <TextField
                        label="Land Size"
                        value={values.landSize}
                        onChange={(e) => setFieldValue('landSize', e.target.value)}
                        type="number"
                        variant="outlined"
                        fullWidth
                      />

                      {/* Land Size Unit */}
                      <Autocomplete
                        options={landSizeUnits}
                        getOptionLabel={(option) => option.label}
                        value={landSizeUnits.find((unit) => unit.value === values.landsizeunit) || null}
                        onChange={(event, newValue) => {
                          setFieldValue('landsizeunit', newValue ? newValue.value : '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit"
                            placeholder="Unit"
                            variant="outlined"
                            sx={{ mt: 2 }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : null}

              {/*Buiness*/}
              {values.propertyType === 'B' ? (
                <>
                  <Grid container spacing={2}>
                    <Grid >
                      <TextField
                        label="Internal Office Code"
                        value={values.internalOfficeCode}
                        onChange={(e) => setFieldValue('internalOfficeCode', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>

                    <Grid >
                      <TextField
                        label="Strata Levies"
                        value={values.strataLevy}
                        onChange={(e) => setFieldValue('strataLevy', e.target.value)}
                        type="number"
                        variant="outlined"
                        slotProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        fullWidth
                      />
                    </Grid>

                    <Grid >
                      <TextField
                        label="Energy Efficiency Rating"
                        value={values.energyRating}
                        onChange={(e) => setFieldValue('energyRating', e.target.value)}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>


                </>
              ) : null}
            </div>

            <Typography className="sectionTitle">Vendor Details</Typography>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <Switch
                checked={values.isSearchContact === 1}
                onChange={(e) => {
                  const newValue = e.target.checked ? 1 : 0;
                  setFieldValue('isSearchContact', newValue);

                  if (newValue === 1) {
                    setFieldValue('isAddContact', 0);
                    setFieldValue('firstNameContact', '');
                    setFieldValue('lastNameContact', '');
                    setFieldValue('contactTypeID', '');
                    setFieldValue('salespersonid', '');
                    setFieldValue('contactEmail', '');
                    setFieldValue('contactNumber', '');
                    setFieldValue('enableSMSBroadcast', 0);
                    setFieldValue('enabledForBroadCast', 0);
                  }
                }}
              />
              <Typography>Search An Existing Contact</Typography>
            </div>
            {!selectedContact ? (
              <>
                {values.isSearchContact === 1 ? (
                  <>
                    <div className="formSection">
                      <Typography className="sectionTitle">Search First and Last Name</Typography>
                      <TextField
                        variant="outlined"
                        label="First Name"
                        value={values.searchFirstName}
                        onChange={(e) => {
                          setFieldValue('searchFirstName', e.target.value);
                          searchContactsFirstLastName(e.target.value, values.searchLastName);
                        }}
                        onFocus={() => {
                          setContactResultsEmail([]);
                          setContactResultsMobile([]);
                          setFieldValue('searchEmail', '');
                          setFieldValue('searchMobile', '');
                          setSelectedContact(null);
                        }}
                        onBlur={handleBlur('searchFirstName')}
                        error={touched.searchFirstName && !!errors.searchFirstName}
                        className="input"
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                      {touched.searchFirstName && errors.searchFirstName && (
                        <Typography color="error" sx={{ fontSize: 12, mt: -1, mb: 1 }}>{errors.searchFirstName}</Typography>
                      )}

                      <TextField
                        variant="outlined"
                        label="Last Name"
                        value={values.searchLastName}
                        onChange={(e) => {
                          setFieldValue('searchLastName', e.target.value);
                          searchContactsFirstLastName(values.searchFirstName, e.target.value);
                        }}
                        onFocus={() => {
                          setContactResultsEmail([]);
                          setContactResultsMobile([]);
                          setFieldValue('searchEmail', '');
                          setFieldValue('searchMobile', '');
                          setSelectedContact(null);
                        }}
                        onBlur={handleBlur('searchLastName')}
                        error={touched.searchLastName && !!errors.searchLastName}
                        className="input"
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                      {touched.searchLastName && errors.searchLastName && (
                        <Typography color="error" sx={{ fontSize: 12, mt: -1, mb: 1 }}>{errors.searchLastName}</Typography>
                      )}
                      {values.searchFirstName?.length > 1 && values.searchLastName?.length > 1 ? (
                        <>
                          {/* Show loading indicator while searching */}
                          {isSearchingName && (
                            <Grid container alignItems="center" sx={{ padding: 2 }}>
                              <CircularProgress size={20} sx={{ marginRight: 1 }} />
                              <Typography sx={{ color: '#555' }}>Searching contacts…</Typography>
                            </Grid>
                          )}

                          {/* Show results when not searching */}
                          {!isSearchingName && contactResultsFirstLastName?.length > 0 && (
                            <Card sx={{ borderRadius: 1, boxShadow: 3, backgroundColor: '#f5f5f5', mt: 1 }}>
                              <CardContent>
                                {contactResultsFirstLastName.map((item) => (
                                  <Button
                                    key={`${item.contactid}-${item.firstname}`}
                                    sx={{
                                      py: 1.5,
                                      px: 2,
                                      mb: 1.5,
                                      backgroundColor: '#f9f9f9',
                                      borderRadius: 1.5,
                                      boxShadow: 1,
                                      fontSize: 16,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      display: 'block',
                                      textAlign: 'left',
                                      textTransform: 'none',
                                    }}
                                    onClick={() => {
                                      setFieldValue('listingcontactId', item.contactid);
                                      setSelectedContact(item);
                                      setcontactResultsFirstLastName([]);
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                      <Typography sx={{ fontSize: 18, fontWeight: '400' }}>
                                        {item.firstname} {item.lastname}
                                      </Typography>
                                      <div>
                                        <Typography
                                          sx={{
                                            backgroundColor:
                                              item.contactstatusid === 1 ? 'green'
                                                : item.contactstatusid === 2 ? 'orange'
                                                  : 'red',
                                            color: 'white',
                                            fontSize: 15,
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            textAlign: 'left',
                                            px: 1.25,
                                            py: 0.5,
                                            display: 'inline-block',
                                          }}
                                        >
                                          {item.contactstatusid === 1 ? 'Current'
                                            : item.contactstatusid === 2 ? 'Archived'
                                              : 'Deleted'}
                                        </Typography>
                                      </div>
                                    </div>
                                    {item.mobile && (
                                      <Typography sx={{ fontSize: 14, color: '#555', mt: 1 }}>
                                        {item.mobile}
                                      </Typography>
                                    )}
                                    {item.email && (
                                      <Typography sx={{ fontSize: 14, color: '#555' }}>
                                        {item.email}
                                        {item.emailVerified === 1 && (
                                          <span style={{ marginLeft: 5, color: 'green' }}>✔</span>
                                        )}
                                      </Typography>
                                    )}
                                    {item.agentfirstname && item.agentlastname && (
                                      <Typography sx={{ fontSize: 14, color: '#555' }}>
                                        {item.agentfirstname} {item.agentlastname}
                                      </Typography>
                                    )}
                                  </Button>
                                ))}
                              </CardContent>
                            </Card>
                          )}
                          {!isSearchingName && contactResultsFirstLastName?.length === 0 && (
                            <Card sx={{ borderRadius: 1, boxShadow: 3, backgroundColor: '#f5f5f5', mt: 1 }}>
                              <CardContent>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
                                  <LocationOnIcon sx={{ fontSize: 48, color: '#ccc' }} />
                                  <Typography sx={{ fontSize: 16, color: '#666', mt: 1, textAlign: 'center' }}>
                                    No contacts found
                                  </Typography>
                                  <Typography sx={{ fontSize: 14, color: '#888', mt: 0.5, textAlign: 'center' }}>
                                    Try adjusting your search terms
                                  </Typography>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </>
                      ) : null}
                    </div>
                    <Typography className="sectionTitle">Search Mobile or Email</Typography>
                    <TextField
                      label="Mobile"
                      variant="outlined"
                      value={values.searchMobile?.trim()}
                      onChange={(e) => {
                        setFieldValue('searchMobile', e.target.value);
                        searchContacts(e.target.value, null, 'mobile');
                      }}
                      onFocus={() => {
                        setContactResultsEmail([]);
                        setcontactResultsFirstLastName([]);
                        setFieldValue('searchEmail', '');
                        setFieldValue('searchFirstName', '');
                        setFieldValue('searchLastName', '');
                        setSelectedContact(null);
                      }}
                      onBlur={handleBlur('searchMobile')}
                      fullWidth
                      sx={{ mb: 2 }}
                    />

                    {values.searchMobile?.trim() && values.searchMobile.length > 5 ? (

                      <>
                        {/* Show loading indicator while searching */}
                        {isSearchingMobile && (
                          <Grid container alignItems="center" sx={{ padding: 2 }}>
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                            <Typography sx={{ color: '#555' }}>Searching contacts…</Typography>
                          </Grid>
                        )}

                        {/* Show results when not searching */}
                        {!isSearchingMobile && contactResultsMobile.length > 0 && (
                          <Card sx={{ borderRadius: 1, boxShadow: 3, backgroundColor: '#f5f5f5', mt: 1 }}>
                            <CardContent>
                              {contactResultsMobile.map((item) => (
                                <Button
                                  key={`${item.contactid}-${item.firstname}`}
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    mb: 1.5,
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: 1.5,
                                    boxShadow: 1,
                                    cursor: 'pointer',
                                    display: 'block',
                                    textAlign: 'left',
                                    textTransform: 'none',
                                  }}
                                  onClick={() => {
                                    setFieldValue('listingcontactId', item.contactid);
                                    setSelectedContact(item);
                                    setContactResultsMobile([]);
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <Typography sx={{ fontSize: 18, fontWeight: '400' }}>
                                      {item.firstname} {item.lastname}
                                    </Typography>
                                    <div>
                                      <Typography
                                        sx={{
                                          backgroundColor:
                                            item.contactstatusid === 1 ? 'green'
                                              : item.contactstatusid === 2 ? 'orange'
                                                : 'red',
                                          color: 'white',
                                          fontSize: 15,
                                          fontWeight: 'bold',
                                          borderRadius: 1,
                                          textAlign: 'left',
                                          px: 1.25,
                                          py: 0.5,
                                          display: 'inline-block',
                                        }}
                                      >
                                        {item.contactstatusid === 1 ? 'Current'
                                          : item.contactstatusid === 2 ? 'Archived'
                                            : 'Deleted'}
                                      </Typography>
                                    </div>
                                  </div>
                                  {item.mobile && (
                                    <Typography sx={{ fontSize: 14, color: '#555', mt: 1 }}>
                                      {item.mobile}
                                    </Typography>
                                  )}
                                  {item.email && (
                                    <Typography sx={{ fontSize: 14, color: '#555' }}>
                                      {item.email}
                                      {item.emailVerified === 1 && (
                                        <span style={{ marginLeft: 5, color: 'green' }}>✔</span>
                                      )}
                                    </Typography>
                                  )}
                                  {item.agentfirstname && item.agentlastname && (
                                    <Typography sx={{ fontSize: 14, color: '#555' }}>
                                      {item.agentfirstname} {item.agentlastname}
                                    </Typography>
                                  )}
                                </Button>
                              ))}
                            </CardContent>
                          </Card>
                        )}
                        {!isSearchingMobile && contactResultsMobile?.length === 0 && (
                          <Card sx={{ borderRadius: 1, boxShadow: 3, backgroundColor: '#f5f5f5', mt: 1 }}>
                            <CardContent>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
                                <LocationOnIcon sx={{ fontSize: 48, color: '#ccc' }} />
                                <Typography sx={{ fontSize: 16, color: '#666', mt: 1, textAlign: 'center' }}>
                                  No contacts found
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: '#888', mt: 0.5, textAlign: 'center' }}>
                                  Try adjusting your search terms
                                </Typography>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    ) : null}

                    <TextField
                      label="Email"
                      value={values.searchEmail}
                      variant="outlined"
                      onChange={(e) => {
                        setFieldValue('searchEmail', e.target.value);
                        searchContacts(null, e.target.value, 'email');
                      }}
                      onFocus={() => {
                        setContactResultsMobile([]);
                        setcontactResultsFirstLastName([]);
                        setFieldValue('searchMobile', '');
                        setFieldValue('searchFirstName', '');
                        setFieldValue('searchLastName', '');
                        setSelectedContact(null);
                      }}
                      onBlur={handleBlur('searchEmail')}
                      sx={{ mt: 2 }}
                      type="email"
                      fullWidth
                    />

                    {values.searchEmail && values.searchEmail.length > 5 ? (
                      <>
                        {/* Show loading indicator while searching */}
                        {isSearchingEmail && (
                          <Grid container alignItems="center" sx={{ padding: 2 }}>
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                            <Typography sx={{ color: '#555' }}>Searching contacts…</Typography>
                          </Grid>
                        )}

                        {/* Show results when not searching */}
                        {!isSearchingEmail && contactResultsEmail.length > 0 && (
                          <Card sx={{ borderRadius: 1, boxShadow: 3, backgroundColor: '#f5f5f5', mt: 1 }}>
                            <CardContent>
                              {contactResultsEmail.map((item) => (
                                <Button
                                  key={`${item.contactid}-${item.firstname}`}
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    mb: 1.5,
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: 1.5,
                                    boxShadow: 1,
                                    cursor: 'pointer',
                                    display: 'block',
                                    textAlign: 'left',
                                    textTransform: 'none',
                                  }}
                                  onClick={() => {
                                    setFieldValue('listingcontactId', item.contactid);
                                    setSelectedContact(item);
                                    setContactResultsEmail([]);
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <Typography sx={{ fontSize: 18, fontWeight: '400' }}>
                                      {item.firstname} {item.lastname}
                                    </Typography>
                                    <div>
                                      <Typography
                                        sx={{
                                          backgroundColor:
                                            item.contactstatusid === 1 ? 'green'
                                              : item.contactstatusid === 2 ? 'orange'
                                                : 'red',
                                          color: 'white',
                                          fontSize: 15,
                                          fontWeight: 'bold',
                                          borderRadius: 1,
                                          textAlign: 'left',
                                          px: 1.25,
                                          py: 0.5,
                                          display: 'inline-block',
                                        }}
                                      >
                                        {item.contactstatusid === 1 ? 'Current'
                                          : item.contactstatusid === 2 ? 'Archived'
                                            : 'Deleted'}
                                      </Typography>
                                    </div>
                                  </div>
                                  {item.mobile && (
                                    <Typography sx={{ fontSize: 14, color: '#555', mt: 1 }}>
                                      {item.mobile}
                                    </Typography>
                                  )}
                                  {item.email && (
                                    <Typography sx={{ fontSize: 14, color: '#555' }}>
                                      {item.email}
                                      {item.emailVerified === 1 && (
                                        <span style={{ marginLeft: 5, color: 'green' }}>✔</span>
                                      )}
                                    </Typography>
                                  )}
                                  {item.agentfirstname && item.agentlastname && (
                                    <Typography sx={{ fontSize: 14, color: '#555' }}>
                                      {item.agentfirstname} {item.agentlastname}
                                    </Typography>
                                  )}
                                </Button>
                              ))}
                            </CardContent>
                          </Card>
                        )}
                        {!isSearchingEmail && contactResultsEmail?.length === 0 && (
                          <Card sx={{ borderRadius: 1, boxShadow: 3, backgroundColor: '#f5f5f5', mt: 1 }}>
                            <CardContent>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
                                <LocationOnIcon sx={{ fontSize: 48, color: '#ccc' }} />
                                <Typography sx={{ fontSize: 16, color: '#666', mt: 1, textAlign: 'center' }}>
                                  No contacts found
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: '#888', mt: 0.5, textAlign: 'center' }}>
                                  Try adjusting your search terms
                                </Typography>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : (
              // when a contact is selected
              <Card sx={{ borderRadius: 1.25, my: 2, backgroundColor: '#f5f5f5', boxShadow: 3 }}>
                <CardHeader
                  title={`${selectedContact.firstname} ${selectedContact.lastname}`}
                  avatar={<LocationOnIcon sx={{ fontSize: 26, color: 'black' }} />}
                />
                <CardContent>
                  {selectedContact.mobile && (
                    <Typography sx={{ fontSize: 16, mb: 0.75 }}>
                      {selectedContact.mobile}
                    </Typography>
                  )}
                  {selectedContact.email && (
                    <Typography sx={{ fontSize: 16, mb: 0.75 }}>
                      {selectedContact.email}
                      {selectedContact.emailVerified === 1 && (
                        <span style={{ marginLeft: 5, color: 'green' }}>✔</span>
                      )}
                    </Typography>
                  )}
                  {selectedContact.agentfirstname && selectedContact.agentlastname && (
                    <Typography sx={{ fontSize: 16 }}>
                      {selectedContact.agentfirstname} {selectedContact.agentlastname}
                    </Typography>
                  )}
                </CardContent>
                <Button
                  variant="text"
                  onClick={() => {
                    setSelectedContact(null);
                    setFieldValue('listingcontactId', '');
                    setFieldValue('searchFirstName', '');
                    setFieldValue('searchLastName', '');
                    setFieldValue('searchMobile', '');
                    setFieldValue('searchEmail', '');
                  }}
                >
                  Change Contact
                </Button>
              </Card>
            )}

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, marginTop: 8 }}>
              <Switch
                checked={values.isAddContact === 1}
                onChange={(e) => {
                  const newValue = e.target.checked ? 1 : 0;
                  setFieldValue('isAddContact', newValue);

                  if (newValue === 1) {
                    setFieldValue('isSearchContact', 0);
                    setFieldValue('searchFirstName', '');
                    setFieldValue('searchLastName', '');
                    setFieldValue('searchMobile', '');
                    setFieldValue('searchEmail', '');
                    setSelectedContact(null);
                    setFieldValue('listingcontactId', '');
                  }
                }}
              />
              <Typography>Add A New Contact</Typography>
            </div>

            {values.isAddContact === 1 ? (
              <div>
                <div className="formSection">
                  <div>
                    <TextField
                      variant="outlined"
                      label="First Name"
                      value={values.firstNameContact}
                      onChange={handleChange('firstNameContact')}
                      onBlur={handleBlur('firstNameContact')}
                      className="input"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    {touched.firstNameContact && errors.firstNameContact && (
                      <Typography color="error" sx={{ fontSize: 12, mt: -1, mb: 1 }}>{errors.firstNameContact}</Typography>
                    )}

                    <TextField
                      variant="outlined"
                      label="Last Name"
                      value={values.lastNameContact}
                      onChange={handleChange('lastNameContact')}
                      onBlur={handleBlur('lastNameContact')}
                      error={touched.lastNameContact && !!errors.lastNameContact}
                      className="input"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    {touched.lastNameContact && errors.lastNameContact && (
                      <Typography color="error" sx={{ fontSize: 12, mt: -1, mb: 1 }}>{errors.lastNameContact}</Typography>
                    )}
                    <TextField
                      variant="outlined"
                      label="Email"
                      value={values.contactEmail}
                      onChange={(e) => {
                        setFieldValue('contactEmail', e.target.value);
                        setFieldValue('enabledForBroadCast', e.target.value?.trim() !== '' ? 1 : 0);
                      }}
                      onBlur={handleBlur('contactEmail')}
                      type="email"
                      error={touched.contactEmail && !!errors.contactEmail}
                      className="input"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    {touched.contactEmail && errors.contactEmail && (
                      <Typography color="error" sx={{ fontSize: 12, mt: -1, mb: 1 }}>{errors.contactEmail}</Typography>
                    )}
                    <TextField
                      variant="outlined"
                      label="Phone"
                      value={values.contactNumber}
                      onChange={(e) => {
                        setFieldValue('contactNumber', e.target.value);
                        setFieldValue('enableSMSBroadcast', e.target.value?.trim() !== '' ? 1 : 0);
                      }}
                      onBlur={handleBlur('contactNumber')}
                      type="tel"
                      error={touched.contactNumber && !!errors.contactNumber}
                      className="input"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    {touched.contactNumber && errors.contactNumber && (
                      <Typography color="error" sx={{ fontSize: 12, mt: -1, mb: 1 }}>{errors.contactNumber}</Typography>
                    )}
                  </div>
                  {/* Contact Type Section */}
                  <Typography className="sectionTitle">Contact Type</Typography>
                  <Grid container spacing={2} className="enquiryGrid">
                    {contactTypes.map((type) => (
                      <Grid  key={type.ContactTypeID}>
                        <div className="switchRow" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography className="switchLabel">{type.Type}</Typography>
                          <Switch
                            checked={values.contactTypeID.split(',').includes(type.ContactTypeID.toString())}
                            onChange={() => {
                              let currentSelection = values.contactTypeID
                                ? values.contactTypeID.split(',').filter(id => id?.trim() !== '')
                                : [];

                              if (type.ContactTypeID.toString() === '6') {
                                if (currentSelection.includes('6')) {
                                  currentSelection = currentSelection.filter(id => id !== '6');
                                } else {
                                  currentSelection = ['6'];
                                }
                              } else {
                                if (currentSelection.includes('6')) {
                                  currentSelection = currentSelection.filter(id => id !== '6');
                                }

                                if (currentSelection.includes(type.ContactTypeID.toString())) {
                                  currentSelection = currentSelection.filter(id => id !== type.ContactTypeID.toString());
                                } else {
                                  currentSelection.push(type.ContactTypeID.toString());
                                }
                              }
                              setFieldValue('contactTypeID', currentSelection.join(','));
                            }}
                            disabled={(values.contactTypeID.includes('6') && type.ContactTypeID.toString() !== '6')}
                          />
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                  {touched.contactTypeID && errors.contactTypeID && (
                    <Typography color="error" sx={{ fontSize: 12, mt: 1 }}>{errors.contactTypeID}</Typography>
                  )}
                </div>

                {/* Salesperson Section */}
                <div className="formSection">
                  <Typography variant="h6" gutterBottom>
                    Enquiry Source
                  </Typography>

                  <Autocomplete
                    options={enquirySource}
                    getOptionLabel={(option) => option.source}
                    value={enquirySource.find((option) => option.contactSourceId === values.contactSourceId) || null}
                    onChange={(event, newValue) => {
                      setFieldValue('contactSourceId', newValue ? newValue.contactSourceId : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Enquiry Source"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.contactSourceId === value.contactSourceId}
                  />

                  <Typography className="sectionTitle">Salesperson</Typography>
                  <Autocomplete
                    options={Salesperson}
                    getOptionLabel={(option) => option.salespersonName}
                    value={Salesperson.find(sp => sp.userid === values.salespersonid) || null}
                    onChange={(event, newValue) => {
                      setFieldValue('salespersonid', newValue ? newValue.userid : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Salesperson"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.userid === value.userid}
                  />
                  {touched.salespersonid && errors.salespersonid && (
                    <Typography color="error" sx={{ fontSize: 12, mt: 1 }}>{errors.salespersonid}</Typography>
                  )}
                </div>

                {/* Broadcast Options */}
                <div className="formSection">
                  <Typography className="sectionTitle">Broadcast Options</Typography>
                  <div className="switchContainer">
                    <div className="switchOption">
                      <Typography>SMS Broadcast</Typography>
                      <Switch
                        checked={values.enableSMSBroadcast === 1}
                        onChange={(e) => setFieldValue('enableSMSBroadcast', e.target.checked ? 1 : 0)}
                      />
                    </div>
                    <div className="switchOption">
                      <Typography>Email Broadcast</Typography>
                      <Switch
                        checked={values.enabledForBroadCast === 1}
                        onChange={(e) => setFieldValue('enabledForBroadCast', e.target.checked ? 1 : 0)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <Button
              variant="contained"
              onClick={handleSubmit}
              className="submitButton"
              disabled={isSavingListing}
              startIcon={isSavingListing ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ mt: 3, p: 2 }}
            >
              {isSavingListing ? 'Saving Listing...' : 'Save Listing'}
            </Button>
          </div>

        )}
      </Formik>
    </LocalizationProvider>
  );
};


export default ListingAdd;